import { first } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AlertService, ConstantService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { PropertyLeadProfileService } from '../property-lead-profile.service';
import { PropertyLeadProfile } from '../property-lead-profile';
import { PropertyLeadProfileComponent } from '../property-lead-profile/property-lead-profile.component';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';
import { Location } from "@angular/common";

/** Column model used by <p-table [columns]="cols"> */
interface ColumnDef { field: string; header: string; width?: string; sortable?: boolean; }

@Component({
    selector: 'app-property-lead-profile-list',
    templateUrl: './property-lead-profile-list.component.html',
    styleUrls: ['./property-lead-profile-list.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }
        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
        .status-pill {
          display: inline-block;
          padding: 2px 6px;
          border-radius: 9999px;
          font-size: 12px;
          line-height: 1;
          color: #fff;
        }
        .owner-block { margin-bottom: .35rem; line-height: 1.25; }
  `],
    standalone: false
})
export class PropertyLeadProfileListComponent implements OnInit {

  constructor(
    public propertyLeadProfileService: PropertyLeadProfileService,
    private alertService: AlertService,
    private messageService: MessageService,
    private location1: Location,
    private confirmationService: ConfirmationService,
    private dialog: MatDialog,
    private constantService: ConstantService,
  ) {
    this.propertyTypes = propertyLeadProfileService.propertyTypes;
    this.countries = this.constantService.countries;

    // Priority map
    (this.propertyLeadProfileService?.targetPriorities ?? []).forEach((p: any) => {
      this.priorityLabelMap.set(Number(p.value), p.name);
    });
    // Listing status map
    (this.propertyLeadProfileService?.listingStatuses ?? []).forEach((s: any) => {
      this.statusLabelMap.set(s.value, s.name);
    });
  }

  /* ----------------------------- Columns ----------------------------- */
  private readonly defaultCols: ColumnDef[] = [
    { field: 'listingStatus',          header: 'Contact?' },
    { field: 'targetPriorityNum',      header: 'Priority' },

    // Owners column (unsortable): details stacked; fixed width
    { field: 'ownerLeadProfileSummaries', header: 'Owners', width: '240px', sortable: false },

    
    { field: 'subjAdrDevelopmentName', header: 'Development' },
    { field: 'emailAddress', header: 'Email' },
    { field: 'mobilePhone', header: 'mobile' },
    { field: 'subjAdrStreet',          header: 'Address' },
    { field: 'subjAdrUnit',            header: 'Unit' },
    { field: 'bedroomNum',            header: 'Beds' },
    { field: 'subjAdrCity',            header: 'City' },
    { field: 'subjAdrState',           header: 'State' },
    { field: 'subjAdrPostalCode',      header: 'Zip' },
    { field: 'fullName',               header: 'Full' },
    { field: 'mailAdrStreet',          header: 'Mail Street' },
    { field: 'mailAdrCity',            header: 'Mail City' },
    { field: 'mailAdrState',           header: 'Mail St' },
    { field: 'mailAdrPostalCode',      header: 'Mail Zip' },
    { field: 'mailAdrCountry',         header: 'Mail Count' },
  ];
  cols: ColumnDef[] = [...this.defaultCols];
  trackByField = (_: number, c: ColumnDef) => c.field;
  private readonly COLS_STORAGE_KEY = 'plp_columns_order_v1';

  private persistCols() {
    try { localStorage.setItem(this.COLS_STORAGE_KEY, JSON.stringify(this.cols)); } catch {}
  }

  onColReorder(event: any) {
    if (event?.columns?.length) {
      this.cols = [...event.columns];
    } else if (typeof event?.dragIndex === 'number' && typeof event?.dropIndex === 'number') {
      const next = [...this.cols];
      const [moved] = next.splice(event.dragIndex, 1);
      next.splice(event.dropIndex, 0, moved);
      this.cols = next;
    }
    this.persistCols();
  }

  resetColumnOrder() {
    this.cols = [...this.defaultCols];
    try { localStorage.removeItem(this.COLS_STORAGE_KEY); } catch {}
    this.persistCols();
    this.messageService.add({ severity: 'success', summary: 'Columns reset', detail: 'Column order restored to default.' });
  }

  /* ----------------------------- Data + UI state ----------------------------- */
  selectedProfiles: any[] = [];
  countries: any;

  @ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;

  @Input() showFilter = true;
  @Input() showPagination = true;

  propertyLeadProfiles: any;
  propertyLeadProfile: PropertyLeadProfile;

  first = 0;
  rows = 50;

  datasource: any;
  recordTotal: number;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];

  formLoading = false;
  propertyTypes: any;

  // Export column config for <app-import-export>
  columns: any[] = [
    { field: 'subjAdrStreet', header: 'Address', width: 'auto' },
    { field: 'title',         header: 'Title',   width: 'auto' },
  ];

  /* ---- Lookups ---- */
  private priorityLabelMap = new Map<number, string>();
  private statusLabelMap = new Map<any, string>();

  getTargetPriorityName(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';
    const asNum = Number(value);
    return this.priorityLabelMap.get(asNum) ?? String(value);
  }
  getListingStatusLabel(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    return this.statusLabelMap.get(value) ?? String(value);
  }

  /** Owner lead status meta for color + label */
  ownerLeadStatusMeta(code: number | null | undefined): { label: string; color: string } {
    const meta: Record<number, { label: string; color: string }> = {
      1: { label: 'Prospect',    color: '#6B7280' }, // gray-500
      2: { label: 'Lead',        color: '#3B82F6' }, // blue-500
      3: { label: 'Opportunity', color: '#F59E0B' }, // amber-500
      4: { label: 'Win',         color: '#10B981' }, // emerald-500
      5: { label: 'Loss',        color: '#EF4444' }, // red-500
      6: { label: 'Contact',     color: '#8B5CF6' }, // violet-500
    };
    if (code == null) return { label: '', color: '#9CA3AF' }; // default gray
    return meta[code] ?? { label: String(code), color: '#9CA3AF' };
  }

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem(this.COLS_STORAGE_KEY);
      if (saved) {
        const parsed: ColumnDef[] = JSON.parse(saved);
        const byField = new Map(this.defaultCols.map(c => [c.field, c]));
        const seen = new Set<string>();
        const restored: ColumnDef[] = [];
        for (const c of parsed) {
          if (byField.has(c.field) && !seen.has(c.field)) {
            restored.push(byField.get(c.field)!);
            seen.add(c.field);
          }
        }
        for (const c of this.defaultCols) {
          if (!seen.has(c.field)) restored.push(c);
        }
        this.cols = restored;
      }
      this.multiSortMeta = [];
    } catch {}
  }

  /* ----------------------------- Export ----------------------------- */
  exportSelected($event: any) {
    const exportRows = (this.propertyLeadProfiles ?? []).map((item: any) => ({
      full_name: item.fullName,
      street_address: item.subjAdrStreet,
      street_address2: item.mailAdrStreet2,
      city: item.mailAdrCity,
      state: item.mailAdrState,
      zip_code: item.mailAdrPostalCode,
      country: item.mailAdrCountry,
      deveopment: item.subjAdrDevelopmentName,
      listingStatus: item.listingStatus,
      targetPriorityNum: item.targetPriorityNum,
      listingStatusLabel: this.getListingStatusLabel(item.listingStatus),
      targetPriorityLabel: this.getTargetPriorityName(item.targetPriorityNum),
      propertyLeadProfileId: item.propertyLeadProfileId
    }));
    this.importExportComponent.exporData($event, exportRows, 'Property Lead Profile Mailing List');
  }

  /* ----------------------------- Editing lifecycle ----------------------------- */
  reloadPage() { window.location.reload(); }
  onCellEditInit(event: any): void { /* no-op */ }
  onCellEditComplete(event: any): void { this.formUpdate(event.data); }
  onCellEditCancel(): void {
    this.messageService.add({ severity: 'info', summary: 'Update Canceled', detail: 'No changes were saved' });
  }

  onRowDelete(event: any) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete Id# ' + event.propertyLeadProfileId + '?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => { this.formDelete(event); },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
      }
    });
  }

  /* ----------------------------- Lazy loading ----------------------------- */
  loadLazy(event: LazyLoadEvent) {
    try {
      this.loading = true;
      this.lazyLoadEvent = event;

      if (Object.keys(event.filters ?? {}).length === 0 && !event.multiSortMeta) {
        event.multiSortMeta = [];
        event.multiSortMeta.push({ field: 'createDate', order: -1 });
      }

      const pageNumber = Math.round((event.first ?? 0) / (event.rows ?? this.rows));
      this.propertyLeadProfileService.findByFilter(
        pageNumber, event.rows,
        event.sortField,
        event.sortOrder,
        event.multiSortMeta,
        event.globalFilter,
        event.filters
      ).pipe(first()).subscribe(
        (res: any) => {
          this.datasource = res.data.reviews;
          this.recordTotal = res.page.recordTotal;
          this.propertyLeadProfiles = res.data.propertyLeadProfiles;
        },
        (err: any) => {
          this.alertService.error(err);
        },
        () => { this.loading = false; }
      );

      if (this.datasource) {
        this.propertyLeadProfiles = this.datasource.slice(event.first, (event.first + event.rows));
      }
    } catch (error) {
      console.log('loadLazy(' + event + ')' + error);
    }
  }

  /* ----------------------------- Tag filtering helper ----------------------------- */
  getStatus() { return; }

  getFilteredTags(tags: any) {
    if (tags == '0') {
      delete this.lazyLoadEvent.filters.propertyLeadProfileId;
      this.loadLazy(this.lazyLoadEvent);
    } else {
      if (tags?.length > 0) {
        const filteredPropertyLeadProfileIds: any[] = [];
        tags.forEach((tag: any) => filteredPropertyLeadProfileIds.push(tag.tagSourceId));
        const idFilter = { 'propertyLeadProfileId': { 'value': filteredPropertyLeadProfileIds, 'matchMode': 'in' } };
        Object.assign(this.lazyLoadEvent.filters, idFilter);
        this.loadLazy(this.lazyLoadEvent);
      } else {
        this.propertyLeadProfiles = [];
      }
    }
  }

  /* ----------------------------- Row click opens editor ----------------------------- */
  onRowSelect(event: any) {
    this.onOpenFormEdit(event.data);
  }
  formAdd() { }

  /* ----------------------------- CRUD ----------------------------- */
  formUpdate(propertyLeadProfile: PropertyLeadProfile) {
    try {
      this.formLoading = true;
      this.propertyLeadProfileService.update(propertyLeadProfile)
        .subscribe(
          (res: any) => {
            this.propertyLeadProfiles.map((plp: any, i: number) => {
              if (plp.propertyLeadProfileId === res.data.propertyLeadProfile.propertyLeadProfileId) {
                this.propertyLeadProfiles[i] = res.data.propertyLeadProfile;
              }
            });
            this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
          },
          (err: any) => {
            this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
          },
          () => { this.formLoading = false; }
        );
    } catch (error) {
      this.alertService.error(error);
    }
  }

  formDelete(propertyLeadProfile: any) {
    try {
      this.formLoading = true;
      this.propertyLeadProfileService.delete(propertyLeadProfile.propertyLeadProfileId)
        .subscribe(
          (res: any) => {
            this.propertyLeadProfiles.splice(this.propertyLeadProfiles.indexOf(propertyLeadProfile), 1);
            this.recordTotal = this.propertyLeadProfiles.length;
            this.messageService.add({
              severity: 'success',
              summary: 'Successful',
              detail: 'PropertyLeadProfile ' + propertyLeadProfile.propertyLeadProfileId + ' Deleted',
              life: 3000
            });
          },
          (err: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Delete Failed',
              detail: 'PropertyLeadProfile ' + propertyLeadProfile.propertyLeadProfileId + ' could not be deleted.  Be sure there are no assigned properties to this bucket.',
              life: 3000
            });
          },
          () => { this.formLoading = false; }
        );
    } catch (error) {
      this.alertService.error(error);
    }
  }

  /* ----------------------------- Dialogs ----------------------------- */
  onOpenFormAdd() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.data = { title: 'Add PropertyLeadProfile' };

    const dialogRef = this.dialog.open(PropertyLeadProfileComponent, dialogConfig);
    dialogRef.componentInstance.doAdd.subscribe(() => {
      this.loadLazy(this.lazyLoadEvent);
      dialogRef.close();
    });
  }

  onOpenFormEdit(propertyLeadProfile: PropertyLeadProfile) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.hasBackdrop = true;
    dialogConfig.data = {
      propertyLeadProfile: propertyLeadProfile,
      title: 'Edit PropertyLeadProfile'
    };

    const dialogRef = this.dialog.open(PropertyLeadProfileComponent, dialogConfig);
    this.location1.go('/property-lead-profile/' + propertyLeadProfile?.propertyLeadProfileId);

    dialogRef.componentInstance.doUpdate.subscribe((data: any) => {
      this.location1.go('/property-lead-profile-list');
      if (data === "close") dialogRef.close();
      this.loadLazy(this.lazyLoadEvent);
    });

    dialogRef.componentInstance.doDelete.subscribe((data: any) => {
      this.deletePropertyLeadProfileCB(data);
      dialogRef.close();
    });
  }

  /* ----------------------------- Dialog callbacks ----------------------------- */
  addPropertyLeadProfileCB(propertyLeadProfile: PropertyLeadProfile) {
    if (!this.propertyLeadProfiles) this.propertyLeadProfiles = [];
    this.propertyLeadProfiles.unshift(propertyLeadProfile);
    this.propertyLeadProfiles = [...this.propertyLeadProfiles];
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'PropertyLeadProfile ' + propertyLeadProfile.propertyLeadProfileId + ' Added',
      life: 3000
    });
  }

  updatePropertyLeadProfileCB(propertyLeadProfile: PropertyLeadProfile) {
    const idx = this.propertyLeadProfiles.findIndex((item: any) => item.propertyLeadProfileId === propertyLeadProfile.propertyLeadProfileId);
    this.propertyLeadProfiles[idx] = propertyLeadProfile;
    this.propertyLeadProfiles = [...this.propertyLeadProfiles];
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'PropertyLeadProfile ' + propertyLeadProfile.propertyLeadProfileId + ' Updated',
      life: 3000
    });
  }

  deletePropertyLeadProfileCB(propertyLeadProfile: PropertyLeadProfile) {
    this.propertyLeadProfiles.splice(this.propertyLeadProfiles.indexOf(propertyLeadProfile), 1);
    this.recordTotal = this.propertyLeadProfiles.length;
    this.propertyLeadProfiles = [...this.propertyLeadProfiles];
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'PropertyLeadProfile ' + propertyLeadProfile.propertyLeadProfileId  + ' Deleted', life: 3000 });
  }

  /* ----------------------------- Paginator helpers ----------------------------- */
  next() { this.first = this.first + this.rows; }
  prev() { this.first = this.first - this.rows; }
  reset() { this.first = 0; }
  isLastPage(): boolean { return this.propertyLeadProfiles ? this.first === (this.propertyLeadProfiles.length - this.rows) : true; }
  isFirstPage(): boolean { return this.propertyLeadProfiles ? this.first === 0 : true; }

  get Statuses() { return this.propertyTypes; }

  getStatusName(propertyLeadProfile: any) {
    if (propertyLeadProfile?.status) {
      const status = this.propertyTypes.find((e: any) => e.value == propertyLeadProfile.status);
      return status?.name ?? '';
    }
    return '';
  }

  showPaginator() { return true; }

  getListingStatusObj(propertyLeadProfile: any) {
    return this.propertyLeadProfileService.listingStatuses.find((item: any) => item.value === propertyLeadProfile.listingStatus);
  }

  /* ----------------------------- Bulk edit ----------------------------- */
  menuItems: MenuItem[] = [
    { label: 'Reset Columns', icon: 'pi pi-refresh', command: () => this.resetColumnOrder() },
    { label: 'Bulk Update', icon: 'pi pi-pencil', command: () => this.onBulkUpdateClick() }
  ];

  showBulkUpdateDialog: boolean = false;
  onBulkUpdateClick() { this.showBulkUpdateDialog = true; }

  // removed firstName/lastName from bulkUpdateData
  bulkUpdateData: any = {
    listingStatus: null,
    targetPriorityNum: null,
    subjAdrDevelopmentName: '',
    subjAdrStreet: '',
    subjAdrUnit: '',
    subjAdrCity: '',
    subjAdrState: '',
    subjAdrPostalCode: '',
    fullName: '',
    mailAdrStreet: '',
    mailAdrCity: '',
    mailAdrState: '',
    mailAdrPostalCode: '',
    mailAdrCountry: ''
  };

  applyBulkUpdate() {
    this.selectedProfiles.forEach((profile: any) => {
      if (this.bulkUpdateData.listingStatus !== null && this.bulkUpdateData.listingStatus !== '') profile.listingStatus = this.bulkUpdateData.listingStatus;
      if (this.bulkUpdateData.targetPriorityNum !== '') profile.targetPriorityNum = this.bulkUpdateData.targetPriorityNum;
      if (this.bulkUpdateData.subjAdrDevelopmentName !== '') profile.subjAdrDevelopmentName = this.bulkUpdateData.subjAdrDevelopmentName;
      if (this.bulkUpdateData.subjAdrStreet !== '') profile.subjAdrStreet = this.bulkUpdateData.subjAdrStreet;
      if (this.bulkUpdateData.subjAdrUnit !== '') profile.subjAdrUnit = this.bulkUpdateData.subjAdrUnit;
      if (this.bulkUpdateData.subjAdrCity !== '') profile.subjAdrCity = this.bulkUpdateData.subjAdrCity;
      if (this.bulkUpdateData.subjAdrState !== '') profile.subjAdrState = this.bulkUpdateData.subjAdrState;
      if (this.bulkUpdateData.subjAdrPostalCode !== '') profile.subjAdrPostalCode = this.bulkUpdateData.subjAdrPostalCode;
      if (this.bulkUpdateData.fullName !== '') profile.fullName = this.bulkUpdateData.fullName;
      if (this.bulkUpdateData.mailAdrStreet !== '') profile.mailAdrStreet = this.bulkUpdateData.mailAdrStreet;
      if (this.bulkUpdateData.mailAdrCity !== '') profile.mailAdrCity = this.bulkUpdateData.mailAdrCity;
      if (this.bulkUpdateData.mailAdrState !== '') profile.mailAdrState = this.bulkUpdateData.mailAdrState;
      if (this.bulkUpdateData.mailAdrPostalCode !== '') profile.mailAdrPostalCode = this.bulkUpdateData.mailAdrPostalCode;
      if (this.bulkUpdateData.mailAdrCountry !== '') profile.mailAdrCountry = this.bulkUpdateData.mailAdrCountry;
    });

    this.bulkUpdatePost(this.selectedProfiles);

    this.bulkUpdateData = {
      listingStatus: null,
      targetPriorityNum: '',
      subjAdrDevelopmentName: '',
      subjAdrStreet: '',
      subjAdrUnit: '',
      subjAdrCity: '',
      subjAdrState: '',
      subjAdrPostalCode: '',
      fullName: '',
      mailAdrStreet: '',
      mailAdrCity: '',
      mailAdrState: '',
      mailAdrPostalCode: '',
      mailAdrCountry: ''
    };
  }

  confirmBulkUpdate() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to update the selected profiles?',
      accept: () => { this.applyBulkUpdate(); },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Update Canceled',
          detail: 'The bulk update was canceled.',
          life: 3000
        });
      }
    });
  }

  bulkUpdatePost(selectedProfiles: any) {
    try {
      this.formLoading = true;
      this.propertyLeadProfileService.bulkUpdate(selectedProfiles)
        .subscribe(
          (res: any) => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Bulk update successful', life: 3000 });
          },
          (err: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Bulk update failed', life: 3000 });
          },
          () => { this.formLoading = false; }
        );
    } catch (error) {
      this.alertService.error(error);
    }
  }
}
